import React from 'react';
import { Box, Typography } from '@mui/material';

export interface EmptyStateProps {
  /** What is missing and, if it helps, what to change. */
  message: string;
  /** A heading above the message, for an empty result the reader must not miss. */
  title?: string;
  /** Hold this height, px, so an empty chart takes the space a full one would. */
  height?: number;
  /**
   * `empty` — there is nothing to show, and that is fine.
   * `error` — something went wrong. Said differently on purpose: a failure
   * that reads as an empty result is a failure nobody goes looking for.
   */
  tone?: 'empty' | 'error';
  /** A way out under the message, e.g. a button that clears the filters. */
  action?: React.ReactNode;
}

/**
 * What a chart or list shows in place of content: a quiet line of text in the
 * space the content would have taken, so the page does not collapse and the
 * reader knows the empty area is deliberate.
 *
 * `tone="error"` for something that failed rather than something that is
 * simply empty.
 */
export function EmptyState({ message, height, tone = 'empty', action, title }: EmptyStateProps) {
  const failed = tone === 'error';
  return (
    <Box
      sx={(t) => ({
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 2,
        py: 3,
        borderRadius: `${t.ds.radius.sm}px`,
        bgcolor: failed ? t.tokens.semantic.dangerSoft : t.tokens.surface.sunken,
      })}
    >
      {title && (
        <Typography
          variant="subtitle1"
          component="p"
          sx={(t) => ({ color: failed ? t.tokens.semantic.danger : t.tokens.text.primary, maxWidth: '56ch', mb: -0.5 })}
        >
          {title}
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={(t) => ({ color: failed ? t.tokens.semantic.danger : t.tokens.text.secondary, maxWidth: '48ch' })}
      >
        {message}
      </Typography>
      {action}
    </Box>
  );
}
